/**
   * Admin Role - matches API response shape
   */
  export interface IAdminRole {
    role_key: string;
    role_name: string;
    role_description: string;
  }

  /**
   * Admin User Input Interface - Domain layer
   * Defines the input structure for creating an AdminUser entity
   * Holds all relevant admin data from the API
   */
  export interface IAdminUserProps {
    sub: string;
    email: string;
    firstName: string;
    lastName: string;
    avatar?: string | null;
    adminRoles: IAdminRole[];
    adminPermissions: string[];
    adminAccessToken: string | null;
    adminRefreshToken: string | null;
    adminContext: IAdminContext;
    // Optional fields returned by the admin "users" listing API.
    userId?: number;
    isActive?: boolean;
    isBot?: boolean;
    kind?: string;
    isDeleted?: boolean;
    organizations?: IAdminUserOrganization[];
  }
  
  interface IAdminContext {
    type: string;
    brand_id: string | null;
    is_global_admin: boolean;
  }

  export interface IAdminUserOrganization {
    organizationId?: number;
    organizationName?: string;
  }

  /**
   * Admin User entity - Domain layer
   * The only user data we use in the app. After Auth0 login we use AdminUser (from admin login API), not Auth0 user data.
   */
  export class AdminUser {
    private readonly _sub: string;
    private readonly _email: string;
    private readonly _firstName: string;
    private readonly _lastName: string;
    private readonly _avatar?: string | null;
    private readonly _fullName: string;
    private readonly _adminRoles: IAdminRole[];
    private readonly _adminPermissions: string[];
    private readonly _adminAccessToken: string | null;
    private readonly _adminRefreshToken: string | null;
    private readonly _adminContext: IAdminContext;
    private readonly _userId?: number;
    private readonly _isActive?: boolean;
    private readonly _isBot?: boolean;
    private readonly _kind?: string;
    private readonly _isDeleted?: boolean;
    private readonly _organizations?: IAdminUserOrganization[];
  
    /**
     * Constructor for AdminUser entity
     */
    constructor(props: IAdminUserProps) {
      this._sub = props.sub;
      this._email = props.email;
      this._firstName = props.firstName;
      this._lastName = props.lastName;
      this._avatar = props.avatar;
      this._fullName = `${this._firstName} ${this._lastName}`.trim();
      this._adminRoles = props.adminRoles;
      this._adminPermissions = props.adminPermissions;
      this._adminAccessToken = props.adminAccessToken;
      this._adminRefreshToken = props.adminRefreshToken;
      this._adminContext = props.adminContext;
      this._userId = props.userId;
      this._isActive = props.isActive;
      this._isBot = props.isBot;
      this._kind = props.kind;
      this._isDeleted = props.isDeleted;
      this._organizations = props.organizations;
    }
  
    get sub(): string {
      return this._sub;
    }
  
    get email(): string {
      return this._email;
    }
  
    get firstName(): string {
      return this._firstName;
    }
  
    get lastName(): string {
      return this._lastName;
    }
  
    get fullName(): string {
      return this._fullName;
    }
  
    get avatar(): string | null | undefined {
      return this._avatar;
    }
  
    get adminRoles(): IAdminRole[] {
      return [...this._adminRoles];
    }

    get adminRoleKeys(): string[] {
      return this._adminRoles.map((r) => r.role_key);
    }
  
    get adminPermissions(): string[] {
      return [...this._adminPermissions];
    }
  
    get adminAccessToken(): string | null {
      return this._adminAccessToken;
    }
  
    get adminRefreshToken(): string | null {
      return this._adminRefreshToken;
    }

    get adminContext(): IAdminContext {
      return this._adminContext;
    }

    get userId(): number | undefined {
      return this._userId;
    }

    get isActive(): boolean | undefined {
      return this._isActive;
    }

    get isBot(): boolean | undefined {
      return this._isBot;
    }

    get kind(): string | undefined {
      return this._kind;
    }

    get isDeleted(): boolean | undefined {
      return this._isDeleted;
    }

    get organizations(): IAdminUserOrganization[] | undefined {
      return this._organizations;
    }
  }
  
  